import { equivalentRouteLocales, isRouteAvailableInLocale } from "./route-equivalence";
import navmenu from "@/data/navmenu.json";
import type { Lang } from "@/lib/types";
import { getBlogSegment } from "@/lib/blog/locales";
import { normalizeBlogPostSlug } from "@/lib/blog/post-slug";
import { buildBlogCategoryPath, resolveBlogCategorySegments } from "@/lib/blog/category-routes";
import {
  getProjectRouteBySlug as getProjectBySlug,
  getServiceRouteBySlug as getServiceBySlug,
  getServiceRouteByTag as getServiceByTag,
} from "./routing-catalog";
import { isStandaloneServiceSubpage, resolveStandaloneServiceSubpage, getServiceSubpageSlug, resolveServiceSubpageKey } from "@/lib/service-subpages";
import { getLocalLandingPage, resolveLocalLandingCity } from "@/lib/local-landing-pages";
import { getCampaignLandingPage, resolveCampaignLandingKey } from "@/lib/campaign-landing-pages";
import { t } from "@/lib/content";
import {
  DEFAULT_LOCALE,
  localePrefix,
  type SiteLocale,
  isSiteLocale,
} from "@/lib/i18n/config";
import {
  getCustomPathByMarketSegment,
  getMarketCustomPathSegment,
  getMarketServiceSlug,
  getMarketSubpageSlug,
  getServiceTagByMarketSlug,
  getSubpageKeyByMarketSlug,
  isMarketLocale,
  isMarketRouteAvailable,
  marketFallbackLocale,
} from "@/lib/i18n/market-pages";

/**
 * Norwegian and Danish links to a page that has no version in that language go
 * to the catalog's fallback page instead (Swedish for Norwegian, English for
 * Danish; see `src/data/norwegian-pages.json` and `danish-pages.json`).
 */
function localeForRoute(route: ResolvedMarketingRoute, locale: SiteLocale): SiteLocale {
  return isMarketLocale(locale) && !isMarketRouteAvailable(locale, route)
    ? marketFallbackLocale(locale) ?? locale
    : locale;
}

export type NavCustomPath =
  | "home"
  | "services"
  | "projects"
  | "about"
  | "contact"
  | "privacy-policy"
  | "blog"
  | "resources"
  | "web-agency"
  | "ai-visibility"
  | "compare"
  | "industries";

export type ResolvedMarketingRoute =
  | { kind: "home" }
  | { kind: "services" }
  | { kind: "service"; serviceTag: string }
  | { kind: "service-subpage"; serviceTag: string; subKey: string }
  | { kind: "projects" }
  | { kind: "project"; projectSlug: string }
  | { kind: "about" }
  | { kind: "contact" }
  | { kind: "privacy-policy" }
  | { kind: "blog" }
  | { kind: "blog-category"; categorySlug: string }
  | { kind: "blog-post"; postSlug: string }
  | { kind: "resources" }
  | { kind: "web-agency" }
  | { kind: "ai-visibility" }
  | { kind: "compare" }
  | { kind: "industries" }
  | { kind: "local-landing"; city: string }
  | { kind: "campaign-landing"; key: string };

type NavmenuItem = (typeof navmenu.main)[number];

function findNavItemByCustomPath(customPath: string): NavmenuItem | undefined {
  return navmenu.main.find((item) => item.en?.customPath === customPath);
}

function getNavEntry(customPath: string, locale: SiteLocale) {
  const item = findNavItemByCustomPath(customPath);
  return item?.[locale];
}

export function getNavSegment(customPath: NavCustomPath, locale: SiteLocale): string {
  const marketSegment = getMarketCustomPathSegment(locale, customPath);
  if (marketSegment) return marketSegment;
  const entry = getNavEntry(customPath, locale);
  return entry?.url ?? customPath;
}

export function parseLocaleFromPathname(pathname: string): {
  locale: SiteLocale | null;
  pathWithoutLocale: string;
} {
  const normalized = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const segments = normalized.split("/").filter(Boolean);

  if (segments.length === 0) {
    return { locale: null, pathWithoutLocale: "/" };
  }

  const maybeLocale = segments[0];
  if (!isSiteLocale(maybeLocale)) {
    return { locale: null, pathWithoutLocale: normalized };
  }

  const rest = segments.slice(1);
  const pathWithoutLocale = rest.length === 0 ? "/" : `/${rest.join("/")}`;
  return { locale: maybeLocale, pathWithoutLocale };
}

export function stripLocalePrefix(pathname: string): {
  locale: SiteLocale | null;
  remainder: string;
} {
  const { locale, pathWithoutLocale } = parseLocaleFromPathname(pathname);
  return { locale, remainder: pathWithoutLocale };
}

function joinLocalizedPath(locale: SiteLocale, ...segments: string[]): string {
  const prefix = localePrefix(locale);
  const tail = segments.filter(Boolean);
  if (tail.length === 0) {
    return prefix || "/";
  }
  return `${prefix}/${tail.join("/")}`;
}

export function buildLocalizedPath(options: {
  locale: SiteLocale;
  customPath: NavCustomPath;
  rest?: string[];
}): string {
  const { customPath, rest = [] } = options;
  const locale = localeForRoute(
    { kind: customPath } as ResolvedMarketingRoute,
    options.locale,
  );

  if (customPath === "home") {
    return localePrefix(locale) || "/";
  }

  const segment = getNavSegment(customPath, locale);
  return joinLocalizedPath(locale, segment, ...rest);
}

export function buildServiceHref(locale: SiteLocale, serviceTag: string): string {
  const service = getServiceByTag(serviceTag);
  if (!service) {
    return buildLocalizedPath({ locale, customPath: "services" });
  }
  const target = localeForRoute({ kind: "service", serviceTag }, locale);
  if (isMarketLocale(target)) {
    return joinLocalizedPath(target, getMarketServiceSlug(target, serviceTag) ?? serviceTag);
  }
  const slug = t(service.slug, target);
  return joinLocalizedPath(target, slug);
}

/** Visitor links must never silently switch to a different language. */
export function getAvailableServiceSubpageHref(locale: SiteLocale, serviceTag: string, subKey: string): string | undefined {
  return isRouteAvailableInLocale({kind: "service-subpage", serviceTag, subKey}, locale)
    ? buildServiceSubpageHref(locale, serviceTag, subKey) : undefined;
}

export function buildServiceSubpageHref(
  locale: SiteLocale,
  serviceTag: string,
  subKey: string,
): string {
  const target = localeForRoute({ kind: "service-subpage", serviceTag, subKey }, locale);
  const slug =
    isMarketLocale(target)
      ? getMarketSubpageSlug(target, serviceTag, subKey) ?? subKey
      : getServiceSubpageSlug(serviceTag, subKey, target);
  return isStandaloneServiceSubpage(serviceTag, subKey, target)
    ? joinLocalizedPath(target, slug)
    : `${buildServiceHref(target, serviceTag)}/${slug}`;
}

export function buildProjectHref(locale: SiteLocale, projectSlug: string): string {
  const target = localeForRoute({ kind: "project", projectSlug }, locale);
  return buildLocalizedPath({ locale: target, customPath: "projects", rest: [projectSlug] });
}

export function buildPathFromResolved(
  route: ResolvedMarketingRoute,
  locale: SiteLocale,
): string {
  switch (route.kind) {
    case "home":
      return localePrefix(locale) || "/";
    case "services":
      return buildLocalizedPath({ locale, customPath: "services" });
    case "service":
      return buildServiceHref(locale, route.serviceTag);
    case "service-subpage":
      return buildServiceSubpageHref(locale, route.serviceTag, route.subKey);
    case "projects":
      return buildLocalizedPath({ locale, customPath: "projects" });
    case "project":
      return buildProjectHref(locale, route.projectSlug);
    case "about":
      return buildLocalizedPath({ locale, customPath: "about" });
    case "contact":
      return buildLocalizedPath({ locale, customPath: "contact" });
    case "privacy-policy":
      return buildLocalizedPath({ locale, customPath: "privacy-policy" });
    case "blog":
      return buildBlogHref(locale);
    case "blog-category":
      return buildBlogCategoryPath(buildBlogHref(locale), route.categorySlug, locale);
    case "blog-post":
      return buildBlogPostHref(route.postSlug, locale);
    case "resources":
      return buildLocalizedPath({ locale, customPath: "resources" });
    case "web-agency":
      return buildLocalizedPath({ locale, customPath: "web-agency" });
    case "ai-visibility":
      return buildLocalizedPath({ locale, customPath: "ai-visibility" });
    case "compare":
      return buildLocalizedPath({ locale, customPath: "compare" });
    case "industries":
      return buildLocalizedPath({ locale, customPath: "industries" });
    case "local-landing": {
      const page = getLocalLandingPage(route.city);
      return page ? `${localePrefix(page.locale)}/${page.slug}` : localePrefix(locale) || "/";
    }
    case "campaign-landing": {
      const page = getCampaignLandingPage(route.key);
      return page ? `${localePrefix(page.locale)}/${page.slug}` : localePrefix(locale) || "/";
    }
    default:
      return localePrefix(locale) || "/";
  }
}

function findNavItemByPublicSegment(
  segment: string,
  locale: SiteLocale,
): { customPath: NavCustomPath } | null {
  const marketPath = getCustomPathByMarketSegment(locale, segment);
  if (marketPath) return { customPath: marketPath };
  for (const item of navmenu.main) {
    const entry = item[locale];
    if (!entry?.customPath) continue;
    if (entry.url === segment) {
      return { customPath: entry.customPath as NavCustomPath };
    }
  }
  return null;
}

/** Service detail pages implemented under the locale catch-all dispatcher. */
export const ROUTABLE_SERVICE_PAGE_TAGS = new Set([
  "web-design",
  "lead-gen",
  "seo",
  "sem",
  "ai-automation",
  "ai-visibility",
]);

const SERVICE_PAGE_TAGS = ROUTABLE_SERVICE_PAGE_TAGS;

function tryResolveServiceRoute(
  slugSegment: string,
  rest: string[],
  locale: SiteLocale,
): ResolvedMarketingRoute | null {
  if (isMarketLocale(locale)) {
    return tryResolveMarketServiceRoute(locale, slugSegment, rest);
  }
  const service = getServiceBySlug(slugSegment, locale);
  if (!service || !SERVICE_PAGE_TAGS.has(service.tag)) {
    return null;
  }
  if (rest.length === 0) {
    return { kind: "service", serviceTag: service.tag };
  }
  if (rest.length === 1) {
    const subKey = resolveServiceSubpageKey(service.tag, rest[0]);
    if (subKey && isRouteAvailableInLocale({ kind: "service-subpage", serviceTag: service.tag, subKey }, locale)) {
      return {
        kind: "service-subpage",
        serviceTag: service.tag,
        subKey,
      };
    }
  }
  return null;
}

/** Norwegian and Danish service URLs only match pages that exist in that language. */
function tryResolveMarketServiceRoute(
  locale: SiteLocale,
  slugSegment: string,
  rest: string[],
): ResolvedMarketingRoute | null {
  const serviceTag = getServiceTagByMarketSlug(locale, slugSegment);
  if (!serviceTag || !SERVICE_PAGE_TAGS.has(serviceTag)) {
    return null;
  }
  if (rest.length === 0) {
    return { kind: "service", serviceTag };
  }
  if (rest.length === 1) {
    const subKey = getSubpageKeyByMarketSlug(locale, serviceTag, rest[0]);
    if (subKey) {
      return { kind: "service-subpage", serviceTag, subKey };
    }
  }
  return null;
}

export function isUnprefixedBlogPath(pathOnly: string): boolean {
  return pathOnly === "/blog" || pathOnly.startsWith("/blog/");
}

/** Paths that stay unprefixed (no locale segment). */
const UNPREFIXED_INTERNAL_PATHS = new Set(["/blog"]);

/** Locale for unprefixed `/blog` and `/blog/[slug]` (English legacy URLs). */
export const UNPREFIXED_BLOG_LOCALE: SiteLocale = "en";

/** Effective site locale from a public pathname (handles unprefixed blog). */
export function localeFromPathname(pathname: string): SiteLocale {
  const { locale, pathWithoutLocale } = parseLocaleFromPathname(pathname);
  if (locale) {
    return locale;
  }
  if (isUnprefixedBlogPath(pathWithoutLocale)) {
    return UNPREFIXED_BLOG_LOCALE;
  }
  return DEFAULT_LOCALE;
}

/** English uses unprefixed `/blog`; other locales use nav segment (e.g. `/sv/blogg`). */
export function buildBlogHref(locale: Lang = UNPREFIXED_BLOG_LOCALE): string {
  if (locale === UNPREFIXED_BLOG_LOCALE) {
    return "/blog";
  }
  return `/${locale}/${getBlogSegment(locale)}`;
}

export function buildBlogPostHref(
  postSlug: string,
  locale: Lang = UNPREFIXED_BLOG_LOCALE,
): string {
  const slug = encodeURIComponent(normalizeBlogPostSlug(postSlug));
  if (locale === UNPREFIXED_BLOG_LOCALE) {
    return `/blog/${slug}`;
  }
  return `${buildBlogHref(locale)}/${slug}`;
}

export function isRoutableServiceTag(tag: string): boolean {
  return ROUTABLE_SERVICE_PAGE_TAGS.has(tag);
}

export function resolveMarketingRoute(
  pathWithoutLocale: string,
  locale: SiteLocale,
): ResolvedMarketingRoute | null {
  const normalized =
    pathWithoutLocale === "/" ? "" : pathWithoutLocale.replace(/^\//, "").replace(/\/$/, "");

  if (!normalized) {
    return { kind: "home" };
  }

  const segments = normalized.split("/").filter(Boolean);
  const [first, ...rest] = segments;

  if (first === "blog" || first === "blogg") {
    const categorySlug = resolveBlogCategorySegments(rest, locale);
    if (categorySlug) return { kind: "blog-category", categorySlug };
    if (rest.length === 0) return { kind: "blog" };
    if (rest.length === 1) {
      let postSlug = rest[0];
      try {
        postSlug = decodeURIComponent(postSlug);
      } catch {
        return null;
      }
      return { kind: "blog-post", postSlug };
    }
    return null;
  }

  const city = rest.length === 0 ? resolveLocalLandingCity(first, locale) : null;
  if (city) return { kind: "local-landing", city };

  const campaign = resolveCampaignLandingKey([first, ...rest].join("/"), locale);
  if (campaign) return { kind: "campaign-landing", key: campaign };

  const standalone = rest.length === 0 ? resolveStandaloneServiceSubpage(first, locale) : null;
  if (standalone) return {kind: "service-subpage", ...standalone};

  const directService = tryResolveServiceRoute(first, rest, locale);
  if (directService) {
    return directService;
  }

  const navHit = findNavItemByPublicSegment(first, locale);
  if (!navHit) {
    return null;
  }

  const { customPath } = navHit;

  if (customPath === "services") {
    if (rest.length === 0) {
      return { kind: "services" };
    }
    const nestedService = tryResolveServiceRoute(rest[0], rest.slice(1), locale);
    if (nestedService) {
      return nestedService;
    }
    return null;
  }

  if (customPath === "projects") {
    if (rest.length === 0) {
      return { kind: "projects" };
    }
    if (rest.length === 1) {
      const project = getProjectBySlug(rest[0], locale);
      if (project) {
        return { kind: "project", projectSlug: rest[0] };
      }
    }
    return null;
  }

  if (customPath === "blog") {
    const categorySlug = resolveBlogCategorySegments(rest, locale);
    if (categorySlug) return { kind: "blog-category", categorySlug };
    if (rest.length === 0) {
      return { kind: "blog" };
    }
    if (rest.length === 1) {
      let postSlug = rest[0];
      try {
        postSlug = decodeURIComponent(postSlug);
      } catch {
        return null;
      }
      return { kind: "blog-post", postSlug };
    }
    return null;
  }

  if (rest.length > 0) {
    return null;
  }

  switch (customPath) {
    case "about":
      return { kind: "about" };
    case "contact":
      return { kind: "contact" };
    case "privacy-policy":
      return { kind: "privacy-policy" };
    case "resources":
      return { kind: "resources" };
    case "web-agency":
      return { kind: "web-agency" };
    case "ai-visibility":
      return { kind: "ai-visibility" };
    case "compare":
      return { kind: "compare" };
    case "industries":
      return { kind: "industries" };
    case "home":
      return { kind: "home" };
    default:
      return null;
  }
}

/** Resolve English logical paths (`/services/seo`, `/about`, …) to a marketing route. */
export function resolveLogicalEnglishPath(pathname: string): ResolvedMarketingRoute | null {
  const normalized = pathname.replace(/\/$/, "") || "/";
  if (normalized === "/") {
    return { kind: "home" };
  }

  const segments = normalized.split("/").filter(Boolean);
  const [first, ...rest] = segments;

  const directService = tryResolveServiceRoute(first, rest, "en");
  if (directService) {
    return directService;
  }

  const navHit = findNavItemByPublicSegment(first, "en");
  if (!navHit) {
    return null;
  }

  const { customPath } = navHit;

  if (customPath === "services") {
    if (rest.length === 0) return { kind: "services" };
    const nestedService = tryResolveServiceRoute(rest[0], rest.slice(1), "en");
    if (nestedService) {
      return nestedService;
    }
    return null;
  }

  if (customPath === "projects") {
    if (rest.length === 0) return { kind: "projects" };
    if (rest.length === 1) {
      if (getProjectBySlug(rest[0], "en")) {
        return { kind: "project", projectSlug: rest[0] };
      }
    }
    return null;
  }

  if (customPath === "blog") {
    if (rest.length === 0) return { kind: "blog" };
    if (rest.length === 1) return { kind: "blog-post", postSlug: rest[0] };
    return null;
  }

  if (rest.length > 0) return null;

  switch (customPath) {
    case "about":
      return { kind: "about" };
    case "contact":
      return { kind: "contact" };
    case "privacy-policy":
      return { kind: "privacy-policy" };
    case "resources":
      return { kind: "resources" };
    case "web-agency":
      return { kind: "web-agency" };
    case "ai-visibility":
      return { kind: "ai-visibility" };
    case "compare":
      return { kind: "compare" };
    case "industries":
      return { kind: "industries" };
    case "home":
      return { kind: "home" };
    default:
      return null;
  }
}

/** Canonical English path segment for nav/footer theme helpers (no locale prefix). */
export function buildLogicalEnglishPathFromRoute(
  route: ResolvedMarketingRoute,
): string {
  switch (route.kind) {
    case "home":
      return "/";
    case "services":
      return "/services";
    case "service": {
      const service = getServiceByTag(route.serviceTag);
      // Logical theme keys are stable, independent of SEO-facing public URLs.
      const logicalSlugs: Record<string, string> = {"web-design": "website-design", "lead-gen": "lead-generation", sem: "advertising"};
      const slug = logicalSlugs[route.serviceTag] ?? (service ? t(service.slug, "en") : route.serviceTag);
      return `/${slug}`;
    }
    case "service-subpage":
      return `${buildLogicalEnglishPathFromRoute({
        kind: "service",
        serviceTag: route.serviceTag,
      })}/${route.subKey}`;
    case "projects":
      return "/projects";
    case "project":
      return `/projects/${route.projectSlug}`;
    case "about":
      return "/about";
    case "contact":
      return "/contact";
    case "privacy-policy":
      return "/privacy-policy";
    case "blog":
      return "/blog";
    case "blog-category":
      return buildBlogCategoryPath("/blog", route.categorySlug, "en");
    case "blog-post":
      return buildBlogPostHref(route.postSlug);
    case "resources":
      return "/resources";
    case "web-agency":
      return "/web-agency";
    case "ai-visibility":
      return "/ai-visibility";
    case "compare":
      return "/compare";
    case "industries":
      return "/industries";
    case "local-landing":
      // City pages share the webbyrå hub chrome (dark video hero).
      return buildLogicalEnglishPathFromRoute({ kind: "service", serviceTag: "web-design" });
    case "campaign-landing":
      // Ad landing pages share the AI automation hub chrome.
      return buildLogicalEnglishPathFromRoute({ kind: "service", serviceTag: "ai-automation" });
    default: {
      // A new route kind must declare its logical path; never silently classify it as home.
      const unhandledRoute: never = route;
      throw new Error(`Unhandled logical route: ${JSON.stringify(unhandledRoute)}`);
    }
  }
}

/**
 * Map a public pathname (with or without locale prefix) to the English logical path
 * used by nav theme helpers (`/services`, `/about`, …).
 */
export function toLogicalPathname(pathname: string): string {
  const { pathWithoutLocale } = parseLocaleFromPathname(pathname);
  const activeLocale = localeFromPathname(pathname);
  const route = resolveMarketingRoute(pathWithoutLocale, activeLocale);
  if (!route) {
    return pathname;
  }
  return buildLogicalEnglishPathFromRoute(route);
}

export function matchesLocalizedNavPath(
  href: string,
  locale: SiteLocale,
  customPath: NavCustomPath,
): boolean {
  return href === buildLocalizedPath({ locale, customPath });
}

/** Same logical page URL in another site locale (for language switcher + alternates). */
export function buildAlternateLocaleHref(
  pathname: string,
  targetLocale: SiteLocale,
): string {
  const { pathWithoutLocale } = parseLocaleFromPathname(pathname);
  const activeSource = localeFromPathname(pathname);

  const route = resolveMarketingRoute(pathWithoutLocale, activeSource);
  if (route) {
    if (!equivalentRouteLocales(route, activeSource).includes(targetLocale)) return localePrefix(targetLocale) || "/";
    return buildPathFromResolved(route, targetLocale);
  }

  const logical = toLogicalPathname(pathname);
  const logicalRoute = resolveLogicalEnglishPath(logical);
  if (logicalRoute) {
    return buildPathFromResolved(logicalRoute, targetLocale);
  }

  return localePrefix(targetLocale) || "/";
}

export function localizeInternalHref(href: string, locale: SiteLocale): string {
  if (
    !href.startsWith("/") ||
    href.startsWith("//") ||
    href.startsWith("/api") ||
    href.startsWith("/dev")
  ) {
    return href;
  }

  const [pathPart, hash = ""] = href.split("#");
  const [pathOnly, query = ""] = pathPart.split("?");
  const withLeading = pathOnly.startsWith("/") ? pathOnly : `/${pathOnly}`;

  const { locale: embedded } = parseLocaleFromPathname(withLeading);
  if (embedded) {
    if (embedded === "en") {
      const rootPath = pathOnly.replace(/^\/en(?=\/|$)/, "") || "/";
      return `${rootPath}${query ? `?${query}` : ""}${hash ? `#${hash}` : ""}`;
    }
    return href;
  }

  if (UNPREFIXED_INTERNAL_PATHS.has(withLeading) || isUnprefixedBlogPath(withLeading)) {
    return href;
  }

  const route = resolveLogicalEnglishPath(withLeading);
  if (!route) {
    const fallback = `${localePrefix(locale)}${withLeading === "/" ? "" : withLeading}`;
    const q = query ? `?${query}` : "";
    const h = hash ? `#${hash}` : "";
    return `${fallback || "/"}${q}${h}`;
  }

  const localized = buildPathFromResolved(route, locale);
  const q = query ? `?${query}` : "";
  const h = hash ? `#${hash}` : "";
  return `${localized}${q}${h}`;
}

export function listStaticMarketingRoutes(): ResolvedMarketingRoute[] {
  const routes: ResolvedMarketingRoute[] = [
    { kind: "home" },
    { kind: "services" },
    { kind: "projects" },
    { kind: "about" },
    { kind: "contact" },
    { kind: "privacy-policy" },
    { kind: "blog" },
    { kind: "resources" },
    { kind: "web-agency" },
    { kind: "compare" },
    { kind: "industries" },
  ];

  for (const tag of SERVICE_PAGE_TAGS) {
    routes.push({ kind: "service", serviceTag: tag });
  }

  return routes;
}
